import { useContext } from "react";

import { UserContext } from "@/app/providers";
import { SATOSPortal } from "@/data-adapter/SATOSPortal";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { CreateCompanyRequest } from "@/model/Company/CreateCompanyRequest";
import { ChangeUserOrganizationRequest, User } from "@/model/User/User";

import { ApplicationQueryKeys } from "@/service/ApplicationQueries";
import { ModulesQueryKeys } from "@/service/Settings/ModulesQueries";

export const useMutateUserOrganization = () => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (request: ChangeUserOrganizationRequest) =>
			SATOSPortal.ChangeUserOrganization(request),
		onSettled: () => {
			void queryClient.invalidateQueries({
				queryKey: [ApplicationQueryKeys.user],
			});
			void queryClient.invalidateQueries({
				queryKey: [ModulesQueryKeys.organizationModules],
			});
		},
	});

	return mutation.mutateAsync;
};

export const useMutateUpdateUser = () => {
	const user = useContext(UserContext);
	const queryClient = useQueryClient();
	const { mutateAsync: UpdateUser } = useMutation({
		mutationFn: ({ request, id }: { request: Partial<User>; id: number }) => {
			return SATOSPortal.UpdateUser(request, id);
		},
		onSuccess: () => {
			void queryClient.invalidateQueries({
				queryKey: [ApplicationQueryKeys.user, user?.CompanyId],
			});
		},
		onSettled: () => {
			void queryClient.invalidateQueries({
				queryKey: [ApplicationQueryKeys.user],
			});
		},
	});
	return UpdateUser;
};

export const useMutateCopyUserSettings = () => {
	const queryClient = useQueryClient();
	const { mutateAsync: CopyUserSettings } = useMutation({
		mutationFn: (request: { targetUser: number; sourceUser: number }) => {
			return SATOSPortal.CopyUserSettings(request);
		},
		onSuccess: () => {
			void queryClient.invalidateQueries({
				queryKey: [ApplicationQueryKeys.user],
			});
		},
		onSettled: () => {
			void queryClient.invalidateQueries({
				queryKey: [ApplicationQueryKeys.user],
			});
		},
	});
	return CopyUserSettings;
};

export const useMutateCreateOrganization = () => {
	const user = useContext(UserContext);
	const queryClient = useQueryClient();
	const { mutateAsync: CreateOrganization } = useMutation({
		mutationFn: (request: CreateCompanyRequest) => {
			return SATOSPortal.CreateOrganization(request);
		},
		onSuccess: () => {
			void queryClient.invalidateQueries({
				queryKey: [ApplicationQueryKeys.companies],
			});
		},
		onSettled: () => {
			void queryClient.invalidateQueries({
				queryKey: [ApplicationQueryKeys.companies, user?.CompanyId],
			});
		},
	});

	return CreateOrganization;
};
